import { useEffect, useRef, useState } from "react";
import {
  ChartNoAxesCombined,
  CirclePlus,
  ExternalLink,
  Settings,
  Trash2,
} from "lucide-react";
import {
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPE_LABELS,
  labelFrom,
} from "../../../lib/labels";
import { withInteractiveReadTimeout } from "../../../lib/requestTimeout";
import { openTelegramLink } from "../../../lib/tg";
import { sponsorApi } from "../lib/api";

// Минимальный тип ресурса для отображения в списке
type Resource = {
  id: number;
  name: string;
  type: string;
  status: string;
  url?: string | null;
};

type Props = {
  onCreate: () => void;
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  onStats: (resource: { id: number; name: string }) => void;
};

export function ResourcesList({ onCreate, onEdit, onView, onStats }: Props) {
  const [items, setItems] = useState<Resource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(
    null,
  );
  const loadGeneration = useRef(0);

  async function load() {
    const generation = ++loadGeneration.current;
    setItems(null);
    setError(null);
    try {
      const r = await withInteractiveReadTimeout((signal) =>
        sponsorApi.listResources(signal),
      );
      if (generation !== loadGeneration.current) return;
      if (r.data) {
        setItems(r.data.items as Resource[]);
        return;
      }
      setItems([]);
      setError("не удалось загрузить ресурсы");
    } catch {
      if (generation === loadGeneration.current) {
        setItems([]);
        setError("не удалось загрузить ресурсы");
      }
    }
  }

  useEffect(() => {
    void load();
    return () => {
      loadGeneration.current += 1;
    };
  }, []);

  async function remove(resource: Resource) {
    setDeletingId(resource.id);
    setConfirmingDeleteId(null);
    setError(null);
    try {
      const r = await sponsorApi.deleteResource(resource.id);
      if (r.error) {
        const detail = (r.error as { detail?: string }).detail;
        setError(detail ?? "не удалось удалить ресурс");
        return;
      }
      setItems((prev) =>
        (prev ?? []).filter((item) => item.id !== resource.id),
      );
    } catch {
      setError("не удалось удалить ресурс");
    } finally {
      setDeletingId(null);
    }
  }

  function requestRemove(resource: Resource) {
    if (confirmingDeleteId !== resource.id) {
      setConfirmingDeleteId(resource.id);
      return;
    }
    void remove(resource);
  }

  function openResource(resource: Resource) {
    if (!resource.url) return;
    setConfirmingDeleteId(null);
    openTelegramLink(resource.url);
  }

  if (items === null && !error) {
    return <div className="tp-screen">загрузка...</div>;
  }

  return (
    <div className="tp-screen tp-sponsor-menu-screen">
      <div className="tp-hero text-center">
        <h1 className="tp-title">меню</h1>
        <p className="tp-subtitle mx-auto">
          управление ресурсами и параметрами размещения
        </p>
      </div>

      <button className="tp-button-primary w-full" onClick={onCreate}>
        <CirclePlus size={18} strokeWidth={2.1} />
        <span>добавить ресурс</span>
      </button>

      {error ? (
        <div className="tp-alert" role="alert">
          <div>{error}</div>
          <button
            className="tp-button-secondary mt-3"
            onClick={() => void load()}
          >
            повторить
          </button>
        </div>
      ) : null}

      <ul className="space-y-3">
        {items?.map((r) => {
          const isDraft = r.status === "draft";
          return (
            <li
              key={r.id}
              className="tp-card space-y-4 cursor-pointer"
              onClick={() => (isDraft ? onEdit(r.id) : onView(r.id))}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold">
                    {r.name}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {labelFrom(RESOURCE_TYPE_LABELS, r.type)}
                  </div>
                </div>
                <span className="tp-chip shrink-0">
                  {labelFrom(RESOURCE_STATUS_LABELS, r.status)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  className="tp-button-secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    openResource(r);
                  }}
                  disabled={!r.url}
                >
                  <ExternalLink size={16} strokeWidth={2} />
                  <span>открыть</span>
                </button>
                <button
                  className="tp-button-secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmingDeleteId(null);
                    isDraft ? onEdit(r.id) : onView(r.id);
                  }}
                >
                  <Settings size={16} strokeWidth={2} />
                  <span>настройки</span>
                </button>
                <button
                  className="tp-button-secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmingDeleteId(null);
                    onStats({ id: r.id, name: r.name });
                  }}
                >
                  <ChartNoAxesCombined size={16} strokeWidth={2} />
                  <span>статистика</span>
                </button>
                <button
                  className="tp-button-secondary"
                  onClick={(event) => {
                    event.stopPropagation();
                    requestRemove(r);
                  }}
                  disabled={deletingId === r.id}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  <span>
                    {deletingId === r.id
                      ? "удаляем..."
                      : confirmingDeleteId === r.id
                        ? "подтвердить"
                        : "удалить"}
                  </span>
                </button>
              </div>
            </li>
          );
        })}
        {items?.length === 0 && (
          <li className="tp-empty">
            <div className="text-lg font-semibold">ресурсов пока нет</div>
            <p className="text-sm text-muted-foreground">
              добавьте канал, чат или бот и отправьте его на модерацию
            </p>
            <button className="tp-button-primary w-full" onClick={onCreate}>
              создать первый ресурс
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}
